import React, { useState, useEffect } from 'react';
import { getHarvestConfig, updateHarvestConfig } from '../../services/harvestingService';
import { getManagedLayers } from '../../services/geographyService';
import SpeciesImport from './SpeciesImport';
import SpeciesAutocomplete from './SpeciesAutocomplete';
import { DEFAULT_RESOURCE_USES, DEFAULT_OBSERVED_SIGNS } from './MoreDetailsSection';

// ─── HarvestSetupTab ─────────────────────────────────────────────────────────
const QUESTION_FIELDS = [
  { key: 'subcategory', label: 'Show Sub-type / Method' },
  { key: 'size', label: 'Show Size / Weight' },
  { key: 'sex', label: 'Show Sex (animal categories only)' },
  { key: 'condition', label: 'Show Condition' },
  { key: 'rowNotes', label: 'Show Row Notes' },
  { key: 'abundance', label: 'Show Abundance field' },
  { key: 'observations', label: 'Show Observations field' },
  { key: 'traditionalKnowledge', label: 'Show Traditional Knowledge field' },
  { key: 'isTraditionalUse', label: 'Show Traditional Use toggle' },
  { key: 'locationDescription', label: 'Show Location Description field' },
  { key: 'uses', label: 'Show Resource Uses' },
  { key: 'reproductiveSignsObserved', label: 'Show Signs Observed' },
  { key: 'unusualConditions', label: 'Show Unusual Conditions' },
  { key: 'followUpRequested', label: 'Show Follow-up Request' },
];

const optionListText = (options) => options.map((option) => `${option.value} | ${option.label}`).join('\n');
const parseOptionList = (text) => text.split(/\r?\n/).map((line) => {
  const separator = line.indexOf('|');
  return separator < 0 ? { label: line.trim() }
    : { value: line.slice(0, separator).trim(), label: line.slice(separator + 1).trim() };
}).filter((option) => option.label);

const HS = {
  section: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '18px 20px',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 4px',
  },
  sectionHint: {
    fontSize: 12,
    color: '#64748b',
    margin: '0 0 14px',
    lineHeight: 1.6,
  },
  catBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  catTab: (active) => ({
    padding: '6px 14px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: active ? '2px solid #2563eb' : '2px solid #e2e8f0',
    background: active ? '#eff6ff' : '#f8fafc',
    color: active ? '#1d4ed8' : '#475569',
    transition: 'all 0.1s',
  }),
  addCatBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: '2px dashed #cbd5e1',
    background: '#f8fafc',
    color: '#64748b',
  },
  editPanel: {
    background: '#f0f7ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    padding: '16px',
    marginBottom: 4,
  },
  inlineField: {
    marginBottom: 12,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  input: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 13,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 13,
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  dangerBtn: {
    padding: '5px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid #fca5a5',
    background: '#fef2f2',
    color: '#b91c1c',
  },
};

const HarvestSetupTab = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [config, setConfig] = useState(null);
  const [assignmentSpecies, setAssignmentSpecies] = useState(null);
  const [assignmentError, setAssignmentError] = useState('');
  const [selectedCatIdx, setSelectedCatIdx] = useState(0);
  const [mapLayers, setMapLayers] = useState([]);

  useEffect(() => {
    let alive = true;
    getManagedLayers(tenantId)
      .then((res) => { if (alive) setMapLayers(res.data || []); })
      .catch(() => { if (alive) setMapLayers([]); });
    return () => { alive = false; };
  }, [tenantId]);

  const withKeywordText = (rules) => (rules || []).map((rule) => ({ ...rule, keywordsText: (rule.keywords || []).join(', ') }));

  const updateLayerRule = (index, patch) => {
    setConfig((prev) => {
      const rules = [...(prev.layerRules || [])];
      rules[index] = { ...rules[index], ...patch };
      return { ...prev, layerRules: rules };
    });
  };

  const toggleRuleList = (index, field, value) => {
    setConfig((prev) => {
      const rules = [...(prev.layerRules || [])];
      const list = new Set(rules[index][field] || []);
      if (list.has(value)) list.delete(value); else list.add(value);
      rules[index] = { ...rules[index], [field]: [...list] };
      return { ...prev, layerRules: rules };
    });
  };

  const addLayerRule = (categoryKey = '') => {
    setConfig((prev) => ({
      ...prev,
      layerRules: [...(prev.layerRules || []), { id: `rule_${Date.now()}`, label: '', categories: categoryKey ? [categoryKey] : [], keywords: [], keywordsText: '', layerIds: [] }],
    }));
  };

  const removeLayerRule = (index) => {
    setConfig((prev) => ({ ...prev, layerRules: (prev.layerRules || []).filter((_, i) => i !== index) }));
  };

  useEffect(() => {
    let alive = true;
    getHarvestConfig(tenantId)
      .then((res) => {
        if (!alive) return;
        const data = res.data || {};
        setConfig({
          ...data,
          environmentalListText: (data.environmentalList || []).join('\n'),
          layerRules: withKeywordText(data.layerRules),
        });
      })
      .catch((err) => { if (alive) setMsg(err.response?.data?.message || 'Failed to load harvest setup'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId]);

  const updateCategory = (index, field, value) => {
    setConfig((prev) => {
      const next = { ...prev, categories: [...(prev.categories || [])] };
      next.categories[index] = { ...next.categories[index], [field]: value };
      const oldKey = prev.categories[index].key || prev.categories[index].referenceKey;
      if (field === 'key') next.categories[index].referenceKey = value ? undefined : oldKey;
      if (field === 'key' && oldKey && value && oldKey !== value) {
        const replaceKey = (list) => list?.map((key) => key === oldKey ? value : key);
        next.speciesCategoryAssignments = Object.fromEntries(Object.entries(prev.speciesCategoryAssignments || {})
          .map(([id, assignment]) => [id, assignment.category === oldKey ? { ...assignment, category: value } : assignment]));
        next.speciesCategoryMap = Object.fromEntries(Object.entries(prev.speciesCategoryMap || {})
          .map(([name, category]) => [name, category === oldKey ? value : category]));
        next.questionVisibility = Object.fromEntries(Object.entries(prev.questionVisibility || {})
          .map(([key, list]) => [key, replaceKey(list)]));
        next.layerRules = (prev.layerRules || []).map((rule) => ({ ...rule, categories: replaceKey(rule.categories) }));
      }
      return next;
    });
  };

  const addCategory = () => {
    setConfig((prev) => ({
      ...prev,
      categories: [...(prev.categories || []), { key: '', label: '', subcategoryLabel: 'Sub-type', subtypes: [] }],
    }));
  };

  const removeCategory = (index) => {
    setConfig((prev) => ({
      ...prev,
      categories: (prev.categories || []).filter((_, i) => i !== index),
    }));
  };

  const toggleFieldCategory = (fieldKey, categoryKey) => {
    setConfig((prev) => {
      const qv = { ...(prev.questionVisibility || {}) };
      const list = Array.isArray(qv[fieldKey]) ? [...qv[fieldKey]] : (prev.categories || []).map((category) => category.key);
      const idx = list.indexOf(categoryKey);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(categoryKey);
      qv[fieldKey] = list;
      return { ...prev, questionVisibility: qv };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const keys = (config.categories || []).map((category) => category.key);
      if (keys.some((key) => !key) || new Set(keys).size !== keys.length) {
        setMsg('Every category needs a unique category key before saving.');
        return;
      }
      const environmentalList = String(config.environmentalListText ?? (config.environmentalList || []).join('\n'))
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        speciesCategoryMap: config.speciesCategoryMap,
        speciesCategoryAssignments: Object.fromEntries(Object.entries(config.speciesCategoryAssignments || {})
          .filter(([, assignment]) => (config.categories || []).some((category) => category.key === assignment.category))),
        questionVisibility: config.questionVisibility,
        categories: (config.categories || []).map((c) => ({
          key: c.key,
          label: c.label,
          subcategoryLabel: c.subcategoryLabel,
          subtypes: c.key === 'environmental'
            ? environmentalList
            : String(c.subtypesCsv ?? (c.subtypes || []).join(', '))
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
        })),
        environmentalList,
        resourceUseOptions: parseOptionList(config.resourceUsesText ?? optionListText(config.resourceUseOptions || DEFAULT_RESOURCE_USES)),
        observedSignOptions: parseOptionList(config.observedSignsText ?? optionListText(config.observedSignOptions || DEFAULT_OBSERVED_SIGNS)),
        layerRules: (config.layerRules || []).map((rule) => ({
          id: rule.id,
          label: rule.label,
          categories: rule.categories || [],
          layerIds: rule.layerIds || [],
          keywords: String(rule.keywordsText ?? (rule.keywords || []).join(', '))
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        })),
        popularSpecies: String(config.popularSpeciesText ?? (config.popularSpecies || []).join('\n'))
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean),
        units: String(config.unitsText ?? (config.units || []).join('\n'))
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean),
        abundanceOptions: String(config.abundanceText ?? (config.abundanceOptions || []).map((o) => o.label).join('\n'))
          .split(/\r?\n/)
          .map((label) => ({ label: label.trim() }))
          .filter((o) => o.label),
      };
      const res = await updateHarvestConfig(tenantId, payload);
      setConfig({
        ...res.data,
        environmentalListText: (res.data.environmentalList || []).join('\n'),
        layerRules: withKeywordText(res.data.layerRules),
        categories: (res.data.categories || []).map((c) => ({ ...c, subtypesCsv: (c.subtypes || []).join(', ') })),
        popularSpeciesText: (res.data.popularSpecies || []).join('\n'),
        unitsText: (res.data.units || []).join('\n'),
        abundanceText: (res.data.abundanceOptions || []).map((o) => o.label).join('\n'),
      });
      setMsg('Harvest setup saved.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="page-loading">Loading harvest setup…</p>;
  if (!config) return <p className="error-msg">{msg || 'Harvest setup unavailable.'}</p>;

  const categories = config.categories || [];
  const activeCat = categories[selectedCatIdx] || null;
  const isEnvCat = activeCat?.key === 'environmental';
  const renderLayerRules = (categoryKey) => (config.layerRules || []).map((rule, index) => {
    if (categoryKey && !(rule.categories || []).includes(categoryKey)) return null;
    const shared = (rule.categories || []).length > 1 || !!(rule.keywordsText || rule.keywords?.length);
    return <div key={rule.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <label>Reference map rule name
        <input style={HS.input} placeholder="e.g. Fisheries zones" value={rule.label || ''}
          onChange={(event) => updateLayerRule(index, { label: event.target.value })} />
      </label>
      {categoryKey ? <>
        {shared && <p style={HS.sectionHint}>Shared rule: changes to its name or layers also affect
          {' '}{(rule.categories || []).filter((key) => key !== categoryKey)
            .map((key) => categories.find((category) => category.key === key)?.label || key).join(', ')}
          {rule.keywordsText || rule.keywords?.length ? ' and species keyword matches' : ''}.
          Use the advanced rules below to change that scope. Removing this category leaves other matches intact;
          a species keyword may still display these layers.</p>}
        <button type="button" style={HS.dangerBtn} onClick={() => {
          if (shared) updateLayerRule(index, { categories: (rule.categories || []).filter((key) => key !== categoryKey) });
          else removeLayerRule(index);
        }}>Remove from this category</button>
      </> : <>
        <p style={HS.sectionHint}>Applies to categories:</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {categories.filter((category) => category.key).map((category) => <label key={category.key}>
            <input type="checkbox" checked={(rule.categories || []).includes(category.key)}
              onChange={() => toggleRuleList(index, 'categories', category.key)} /> {category.label || category.key}
          </label>)}
        </div>
        <label>Or species containing these words (comma-separated)
          <input style={HS.input} value={rule.keywordsText ?? (rule.keywords || []).join(', ')}
            onChange={(event) => updateLayerRule(index, { keywordsText: event.target.value })} />
        </label>
        <button type="button" style={HS.dangerBtn} onClick={() => removeLayerRule(index)}>Delete rule for all matches</button>
      </>}
      <p style={HS.sectionHint}>Show these layers:</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {mapLayers.map((layer) => <label key={layer._id} style={{ fontSize: 13 }}>
          <input type="checkbox" checked={(rule.layerIds || []).includes(layer.layerId)}
            onChange={() => toggleRuleList(index, 'layerIds', layer.layerId)} /> {layer.displayName}
        </label>)}
        {!mapLayers.length && <span>No map layers available yet.</span>}
      </div>
    </div>;
  });

  return (
    <section>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>🌿 Harvest Setup</h3>
          <p>Shape this community's collection app: choose a category below, edit its methods and questions,
            and assign species. Save All Settings when finished.</p>
          <p style={HS.sectionHint}>The shared catalogue identifies species; these settings shape logging.
            Stewards manage area guidance separately. Contributors own their entries; staff flag quality issues, not approve submissions.</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Configure community categories, species-to-category assignments, and field options shown to contributors.
            Changes take effect immediately after saving.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '9px 22px', background: saving ? '#94a3b8' : '#16a34a',
            color: '#fff', border: 'none', borderRadius: 7, fontSize: 14,
            fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : '💾 Save All Settings'}
        </button>
      </div>

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: msg.includes('saved') || msg.includes('Imported') ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${msg.includes('saved') || msg.includes('Imported') ? '#86efac' : '#fca5a5'}`,
          color: msg.includes('saved') || msg.includes('Imported') ? '#166534' : '#b91c1c',
        }}>
          {msg}
        </div>
      )}

      {/* ── CATEGORIES ── */}
      <div style={HS.section}>
        <p style={HS.sectionTitle}>📂 Categories &amp; Sub-types</p>
        <p style={HS.sectionHint}>
          Harvest categories group this community's logging forms (e.g. Fishing, Hunting, Garden Veg).
          They are not species: add items in the shared species catalogue, then assign them below.
          Sub-types are choices within a log, such as From Boat, Foraging or Garden Harvest; they do not create species.
          Select Environmental to edit its report-type list.
        </p>

        {/* Tab bar */}
        <div style={HS.catBar}>
          {categories.map((cat, idx) => (
            <button
              key={`${cat.key || 'new'}-${idx}`}
              type="button"
              style={HS.catTab(idx === selectedCatIdx)}
              aria-pressed={idx === selectedCatIdx}
              onClick={() => { setSelectedCatIdx(idx); setAssignmentSpecies(null); setAssignmentError(''); }}
            >
              {cat.label || cat.key || `Category ${idx + 1}`}
              {!cat.key && <span style={{ marginLeft: 6, fontSize: 10, color: '#ef4444' }}>⚠ needs key</span>}
            </button>
          ))}
          <button type="button" style={HS.addCatBtn} onClick={() => {
            const newIdx = categories.length;
            addCategory();
            setSelectedCatIdx(newIdx);
          }}>
            + Add Category
          </button>
        </div>

        {/* Selected category editor */}
        {activeCat ? (
          <div style={HS.editPanel}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <strong style={{ fontSize: 14, color: '#1e40af' }}>
                Editing: {activeCat.label || activeCat.key || `Category ${selectedCatIdx + 1}`}
              </strong>
              <button
                type="button"
                style={HS.dangerBtn}
                onClick={() => {
                  removeCategory(selectedCatIdx);
                  setSelectedCatIdx(Math.max(0, selectedCatIdx - 1));
                }}
              >
                ✕ Remove Category
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div style={HS.inlineField}>
                <label style={HS.fieldLabel}>Category Key <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(system ID, no spaces)</span></label>
                <input
                  style={HS.input}
                  aria-label="Category key"
                  placeholder="e.g. fishing"
                  value={activeCat.key || ''}
                  onChange={(e) => updateCategory(selectedCatIdx, 'key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                />
              </div>
              <div style={HS.inlineField}>
                <label style={HS.fieldLabel}>Display Label <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(shown to contributors)</span></label>
                <input
                  style={HS.input}
                  aria-label="Category display label"
                  placeholder="e.g. Fishing"
                  value={activeCat.label || ''}
                  onChange={(e) => updateCategory(selectedCatIdx, 'label', e.target.value)}
                />
              </div>
            </div>

            <div style={HS.inlineField}>
              <label style={HS.fieldLabel}>Sub-type Field Label <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(column heading shown above sub-type dropdown)</span></label>
              <input
                style={HS.input}
                placeholder="e.g. Method / Context"
                value={activeCat.subcategoryLabel || ''}
                onChange={(e) => updateCategory(selectedCatIdx, 'subcategoryLabel', e.target.value)}
              />
            </div>

            {isEnvCat ? (
              <div style={HS.inlineField}>
                <label htmlFor="environmental-list" style={HS.fieldLabel}>Environmental List</label>
                <p style={HS.sectionHint}>Report types and concerns shown in the Environmental form.
                  Enter one item per line. Save All Settings to apply.</p>
                <textarea id="environmental-list" rows={8} style={HS.textarea}
                  value={config.environmentalListText ?? (config.environmentalList || []).join('\n')}
                  onChange={(event) => setConfig((previous) => ({ ...previous, environmentalListText: event.target.value }))} />
                <p style={HS.sectionHint}>Count: <strong>{(config.environmentalListText ?? (config.environmentalList || []).join('\n'))
                  .split('\n').filter((item) => item.trim()).length} items</strong></p>
              </div>
            ) : (
              <div style={HS.inlineField}>
                <label style={HS.fieldLabel}>Sub-types <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(comma-separated — these appear in the sub-type dropdown)</span></label>
                <input
                  style={HS.input}
                  placeholder="e.g. From Shore, From Boat, Ice Fishing, Net / Trap, Other"
                  value={activeCat.subtypesCsv ?? (activeCat.subtypes || []).join(', ')}
                  onChange={(e) => updateCategory(selectedCatIdx, 'subtypesCsv', e.target.value)}
                />
                {activeCat.subtypesCsv || (activeCat.subtypes || []).length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {String(activeCat.subtypesCsv ?? (activeCat.subtypes || []).join(', '))
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((s) => (
                        <span key={s} style={{ background: '#dbeafe', color: '#1e40af', borderRadius: 999, padding: '2px 10px', fontSize: 12 }}>
                          {s}
                        </span>
                      ))}
                  </div>
                ) : null}
              </div>
            )}
        <h4>Species assigned to {activeCat.label || activeCat.key || 'this category'}</h4>
        <p style={HS.sectionHint}>These community assignments select collection questions, methods and reference maps.
          Adding an already assigned species moves it here. Removing an assignment restores its shared default;
          it does not delete the species or prevent logging. Unlisted species still use their shared defaults.
          Save All Settings to apply changes; reopen the logger to load them.</p>
        <label htmlFor="category-assignment-species">Species to assign</label>
        <SpeciesAutocomplete canCreate id="category-assignment-species" value={assignmentSpecies?.speciesName || ''}
          onSelect={(species) => { setAssignmentSpecies(species); setAssignmentError(''); }}
          onInputChange={(speciesName) => setAssignmentSpecies({ speciesName })} />
        <button type="button" onClick={() => {
          if (!assignmentSpecies?.speciesId || !activeCat.key) {
            setAssignmentError('Select a catalogue species and a community harvest category.');
            return;
          }
          setConfig((previous) => ({ ...previous, speciesCategoryAssignments: {
            ...(previous.speciesCategoryAssignments || {}),
            [assignmentSpecies.speciesId]: { name: assignmentSpecies.speciesName, category: activeCat.key },
          } }));
          setAssignmentError('');
          setAssignmentSpecies(null);
        }}>Assign species</button>
        {assignmentError && <p role="alert">{assignmentError}</p>}
        <ul>
          {Object.entries(config.speciesCategoryAssignments || {}).filter(([, assignment]) => assignment.category === activeCat.key).map(([id, assignment]) => (
            <li key={id}>
              {assignment.name || id} — {categories.find((category) => category.key === assignment.category)?.label || 'Category removed: reassign before saving'}
              {' '}<button type="button" aria-label={`Remove assignment for ${assignment.name || id}`} onClick={() => setConfig((previous) => ({
                ...previous, speciesCategoryAssignments: Object.fromEntries(Object.entries(previous.speciesCategoryAssignments || {}).filter(([key]) => key !== id)),
              }))}>Use shared default</button>
            </li>
          ))}
        </ul>
        {!Object.values(config.speciesCategoryAssignments || {}).some((assignment) => assignment.category === activeCat.key)
          && <p>No community species assignments in this category yet.</p>}
        <h4>Questions for {activeCat.label || activeCat.key || 'this category'}</h4>
        <p style={HS.sectionHint}>Choose optional questions for species using this category.
          Date, species, location, quantity/unit and outcome remain core fields.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {QUESTION_FIELDS.map((field) => <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={!Array.isArray(config.questionVisibility?.[field.key])
              || config.questionVisibility[field.key].includes(activeCat.key)}
              onChange={() => toggleFieldCategory(field.key, activeCat.key)} />
            {field.label}
          </label>)}
        </div>
        <section aria-label="Category reference map layers">
          <h4>Reference map layers for {activeCat.label || activeCat.key || 'this category'}</h4>
          <p style={HS.sectionHint}>Published species guidance is included automatically. Choose extra reference maps
            for this category, such as fisheries zones for Fishing. Contributors can toggle them off.
            Layers must be enabled, have an active version, allow this community and audience, and enable Harvesting.
            Save All Settings to apply.</p>
          {renderLayerRules(activeCat.key || '__new_category__')}
          {!(config.layerRules || []).some((rule) => rule.categories?.includes(activeCat.key))
            && <p>No extra reference map rules for this category.</p>}
          <button type="button" disabled={!activeCat.key} onClick={() => addLayerRule(activeCat.key)}>+ Add layer rule</button>
        </section>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: '#94a3b8' }}>No categories yet. Click + Add Category to begin.</p>
        )}
      </div>

      <div style={HS.section}>
        <p style={HS.sectionTitle}>Shared species catalogue</p>
        <p style={HS.sectionHint}>Harvesting and Stewardship use the same species IDs, categories and aliases. This is not a separate community list. Importing adds species immediately; it does not publish area guidance or require saving Harvest Setup.</p>
        <SpeciesImport />
      </div>

      <details style={HS.section}>
        <summary>Advanced reference map rules (shared categories / species keywords)</summary>
        <p style={HS.sectionHint}>
          Normally manage reference maps inside the selected category above.
          Use this view for existing keyword-only rules or rules shared by several categories.
          A matching category OR species keyword displays the layers; keywords do not restrict a category match.
          Editing a shared rule affects all its matches. Rules need a category or keyword and at least one layer.
        </p>
        {renderLayerRules()}
        <button type="button" onClick={() => addLayerRule()}>+ Add advanced rule</button>
      </details>

      {/* ── POPULAR SPECIES ── */}
      <div style={HS.section}>
        <p style={HS.sectionTitle}>⭐ Popular Species (shown first)</p>
        <p style={HS.sectionHint}>
          These species appear as quick-pick buttons at the top of the species field so contributors
          can tap them without typing. One per line. Keep this list short — 6 to 10 items works best.
        </p>
        <textarea
          rows={5}
          style={HS.textarea}
          value={config.popularSpeciesText ?? (config.popularSpecies || []).join('\n')}
          onChange={(e) => setConfig((prev) => ({ ...prev, popularSpeciesText: e.target.value }))}
        />
      </div>

      {/* ── CODED LISTS ROW ── */}
      <div style={HS.section}>
        <label htmlFor="resource-uses-list" style={HS.sectionTitle}>Resource uses (select all that apply)</label>
        <p style={HS.sectionHint}>Options for “What was this resource used for?” across all harvest categories.
          Enter one option per line. Existing options use code | label; keep the code when changing the wording
          so stored answers keep their meaning. New options can be entered as a label alone.
          Keep other | Other to allow a written answer. An empty list offers no new choices.
          Removing an option does not erase historical answers.
          Use the category's Show Resource Uses switch to show or hide this question. Save All Settings to apply.</p>
        <textarea id="resource-uses-list" rows={8} style={HS.textarea}
          value={config.resourceUsesText ?? optionListText(config.resourceUseOptions || DEFAULT_RESOURCE_USES)}
          onChange={(event) => setConfig((previous) => ({ ...previous, resourceUsesText: event.target.value }))} />
      </div>
      <div style={HS.section}>
        <label htmlFor="observed-signs-list" style={HS.sectionTitle}>Signs observed (select all that apply)</label>
        <p style={HS.sectionHint}>Choices such as young, eggs, nests, spawning or tracks, shared across harvest categories.
          Enter one choice per line. Keep existing codes when changing labels (code | label);
          new choices can be entered as a label alone. Removing a choice does not erase past observations.
          An empty list offers no new choices. The category's Show Signs Observed switch controls visibility.
          Save All Settings to apply.</p>
        <textarea id="observed-signs-list" rows={6} style={HS.textarea}
          value={config.observedSignsText ?? optionListText(config.observedSignOptions || DEFAULT_OBSERVED_SIGNS)}
          onChange={(event) => setConfig((previous) => ({ ...previous, observedSignsText: event.target.value }))} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div style={HS.section}>
          <p style={HS.sectionTitle}>📦 Units (coded list)</p>
          <p style={HS.sectionHint}>
            These units appear in the Units dropdown. Contributors can also choose Other and type a
            custom value. One unit per line.
          </p>
          <textarea
            rows={6}
            style={HS.textarea}
            value={config.unitsText ?? (config.units || []).join('\n')}
            onChange={(e) => setConfig((prev) => ({ ...prev, unitsText: e.target.value }))}
          />
        </div>
        <div style={HS.section}>
          <p style={HS.sectionTitle}>📊 Observation Abundance (coded list)</p>
          <p style={HS.sectionHint}>
            These options describe how abundant the resource was observed to be — even if nothing was
            harvested. Helps document resource health over time. One label per line.
          </p>
          <textarea
            rows={6}
            style={HS.textarea}
            value={config.abundanceText ?? (config.abundanceOptions || []).map((o) => o.label).join('\n')}
            onChange={(e) => setConfig((prev) => ({ ...prev, abundanceText: e.target.value }))}
          />
        </div>
      </div>

      {/* ── QUESTION VISIBILITY ── */}
      <details style={HS.section}>
        <summary style={HS.sectionTitle}>Compare questions across all categories</summary>
        <p style={HS.sectionHint}>
          These switches control optional questions in the guided logger, including row details and the final notes step.
          Uncheck every category to hide a question everywhere; Save All Settings, then reopen the logger to load changes.
          Date, species, map location, quantity/unit and kept/released outcome remain core fields.
          Shared notes are shown if any catch row permits them and saved only to the rows that permit them.
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          {QUESTION_FIELDS.map((field) => (
            <div key={field.key} role="group" aria-label={field.label} style={{ border: '1px solid #e2e8f0', borderRadius: 7, padding: '10px 14px', background: '#f8fafc' }}>
              <strong style={{ display: 'block', fontSize: 13, marginBottom: 8, color: '#334155' }}>{field.label}</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                {categories.map((cat) => {
                  const allowed = config.questionVisibility?.[field.key];
                  const checked = !Array.isArray(allowed) || allowed.includes(cat.key);
                  return (
                    <label key={`${field.key}-${cat.key}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFieldCategory(field.key, cat.key)}
                        style={{ accentColor: '#2563eb', width: 14, height: 14 }}
                      />
                      {cat.label || cat.key}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </details>

      {/* ── Bottom save ── */}
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 28px', background: saving ? '#94a3b8' : '#16a34a',
            color: '#fff', border: 'none', borderRadius: 7, fontSize: 14,
            fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : '💾 Save All Settings'}
        </button>
      </div>
    </section>
  );
};

export default HarvestSetupTab;
